// ── docs · your innernet ────────────────────────────────────────────────────
// The app, surface by surface: what each place is for, where it lives, what
// every control does. The guides walk a person through these by hand; these
// pages are the reference they link back to. Labels are the app's own words
// (checked against the components on 2026-09-29): when one changes, change it
// here and in components/guides/GuideFigure.tsx in the same pass.
//
// Only what is open to everyone is written here. Rooms and asking netti from
// the corner are still with early members, so they are named, not taught.

import type { DocPageDef } from '../content'

const G = 'your innernet'

export const APP_PAGES: DocPageDef[] = [
  {
    slug: 'dashboard',
    title: 'the dashboard',
    group: G,
    description: 'the crumb, the menu, your spaces, the view chips, the ground: how the place is laid out and how to move in it.',
    updated: '2026-09-29',
    body: `
Your dashboard lives at [innernet.live](/dashboard). It is one navigator, the same on every page, so once you can read it you can read all of it.

:::shot way-head the top of every page: the crumb, your spaces, the line that says where you are, and the view chips.

## the crumb and the menu

Top left, the **crumb** says where you are: \`innernet / yours / projects / lisbon-app\`. Every word in it is a way back. **innernet** returns you to your projects from anywhere.

**≡**, right after the wordmark, opens the whole map of innernet over the page: your spaces under **yours** (including **parked**, which the top row hides), then the homepage, company, docs, community, status. Tap a word to look inside it; tap a page to go there. Esc closes it.

**search** sits in the crumb too. It searches where you are standing: everything from the top, only this project from inside one. Results arrive as you type; ask it a question and it switches to finding the answer for you.

## your spaces

The big words along the top are your **spaces**, in this order:

| space | what it holds |
| --- | --- |
| **projects** | every memory you keep, one folder each |
| **you** | what innernet knows about *you*: the day's summary, your story, the mirror, and the private ledger. [The you space](/docs/you) |
| **memory** | the whole memory at a glance: its size, the last 30 days, netti's folds, how fresh it is, and importing what your other ais know |
| **netti** | the one who keeps it: how grown it is, its look, its sounds |
| **skills** | every page you've marked as a skill, across projects |
| **connections** | every app and agent wired to your memory in one list (narrow it, or see it round netti), extensions, API keys, the npx command |
| **activity** | the river (what happened), your repos, and the inbox. [Inbox & the fold](/docs/inbox-and-fold) |
| **tasks** | every project's tasks in one list |
| **settings** | account, app, in your chats, security, data |

The space you're in slides to the left in full ink. Tap another word, or swipe the row (or the page) left or right, to move.

Under the title, one line describes the place. **Double-click it to write your own**; Enter saves, Esc puts it back. Otherwise netti keeps it current as memory lands.

## view chips

The chips under the description are **views**: different ways of looking at the same place.

| chip | where | what it shows |
| --- | --- | --- |
| **grid** | everywhere | tiles. the default |
| **list** | everywhere | dense rows: name and meta |
| **orbit** | projects | your memories circling netti, sized by how much they hold |
| **story** | projects | your projects told as stories |
| **artifacts** | a project with pages | its living documents |
| **tasks** | a project | its task board |
| **timeline** | a project with history | every commit, newest first |
| **repo** | a code project | the repo's branches and commits, by day. [Repo memory](/docs/repo-memory) |

### changing the order

:::shot way-chips hold, drag, let go. the leftmost chip becomes this place's default view.

Press and hold a chip for about half a second until it lifts, drag it along the row, and let go. The order is saved for that place, on every device, and **the leftmost chip is the view it opens in** from then on. Want projects to always open as a list? Drag **list** to the front.

## the ground

:::shot way-dial the ground dial, open. it sits bottom left on every page.

The round swatch bottom left is the **ground dial**. Tap it and four arcs open: hue, saturation, brightness, and how much gradient the ground carries. The whole page repaints as you drag and saves when you let go. The ink follows: pick something deep and the words turn light on their own. Esc or a tap outside closes it.

## everything else on the page

- **options**, bottom left beside **share**: new folder, new page, rename, park, settings, downloads (markdown, Claude context, generic context, JSON, or everything as a zip), change ground colour. Undo and redo sit at its foot.
- **right-click** a tile for the same things: Settings, Rename, Icon…, Open, New folder, New page, Move…, Park. Right-click empty space for a new folder or page.
- **tap** selects, **double-tap** opens, **double-click a name** renames it. Drag a tile over a folder to move it inside.
- **the status corner**, bottom right, says in plain words what you're pointing at.
- **you**, top right: your name and your face. It opens the you space.

Every shortcut is on [one page](/docs/shortcuts).

## related

- [Finding your way around](/community/guides/starter-find-your-way) (a guide, step by step)
- [Projects](/docs/projects)
`,
  },

  {
    slug: 'projects',
    title: 'projects',
    group: G,
    description: 'inside a memory: its pages, reading and editing them, tasks, living documents, handoffs, and its history.',
    updated: '2026-09-29',
    body: `
A **project** is one memory: a repo, a product, a trip, a book. On the dashboard it is a folder under **projects**, and inside it is everything netti keeps about it.

## making one

Three ways, and most people use the first:

1. **From any connected AI.** Say *save this conversation to a new innernet project called "trip to lisbon"*. It appears on your dashboard while you watch.
2. **From a repo.** \`npx innernet\` inside a git repo starts a memory for it, shaped by netti from the readme and the file tree. [Repo memory](/docs/repo-memory)
3. **By hand.** options → **new folder** (or right-click → New folder). Say *what are you building?*, pick a colour and an icon, and choose whether netti keeps the description fresh (**auto**) or you write it (**manual**). Press **create**.

:::shot first-chat the first memory usually starts in a chat.

## what's inside

:::shot first-projects inside a project: its pages first, then the folders netti keeps for it.

- **pages**, marked *domain*: the dimensions. \`vision\`, \`technical\`, \`pricing\`… Each describes the present.
- **skills**: pages in this project that are skills.
- a folder per kind of **node**: people, decisions, artifacts.
- **artifacts**: living documents a session built.
- **inbox**: fresh captures resting here until netti folds them in.
- **handoffs**: the latest "where I left off", and the ones before it.
- **tasks**: the project's board.

## reading and editing a page

Open a page and it reads like a document, with its sibling pages as tabs along the top. To change it, press the **pencil** or **double-click the words**. The page takes a coloured frame and says **editing memory**; press **save** when it says what you mean, or **cancel** (Esc).

:::shot first-edit editing a page in place. the frame is how you know you're writing to memory.

An edit is a commit like any other. If what you wrote supersedes something, keep one dated line under the page's \`## history\` so nothing is lost. [Why](/docs/branches#the-one-rule-on-trunk)

## the timeline

The **timeline** chip lists every commit, newest first: what it said, what triggered it (\`fold\`, \`mcp\`, \`git\`, \`genesis\`…), and what it touched. Open one for netti's own account of it.

:::shot first-timeline the timeline: every save, fold and commit, newest first.

## tasks

The **tasks** chip is the project's board. Type into **add a task…**, tick things off, double-click a title to rename. Any connected AI reads and writes the same board (\`innernet_task_*\`): statuses are *Not started*, *In progress*, *Done*; priorities *High*, *Medium*, *Low*. The **tasks** space shows every project's tasks at once.

## living documents

During a long session an AI can open an **artifact**: one living document that reorganises itself as findings land (\`innernet_artifact_start\`, then \`append\` as it goes). You'll find them under the **artifacts** chip, the live one marked **live**.

## handoffs

At the end of a session, a good AI leaves a **handoff**: what shipped, what's next, what to watch for. The next session, in any tool, reads it first. They're kept under **handoffs**, the latest on top.

:::shot repo-handoff the next morning, in a different tool.

## parking and forgetting

- **park** (options, or right-click → Park) moves a project out of your way into **parked**, reachable from ≡. **unpark** brings it back. Nothing is lost.
- **forget this…** (options, on a page) shows exactly what would go and asks twice. Forgetting is permanent. [Forgetting](/docs/forgetting)

## folder settings

Right-click → **Settings** reopens the composer: name, colour, icon, description. Changes save as you make them. From there you can also share, park, or forget the whole folder.

## related

- [Your first memory](/community/guides/starter-your-first-memory) (a guide)
- [The Context Map model](/docs/concepts)
- [Capture protocol](/docs/capture-protocol)
`,
  },

  {
    slug: 'you',
    title: 'the you space',
    group: G,
    description: 'your day read back to you, the mirror, and the private ledger: every fact innernet holds about you, and who can see it.',
    updated: '2026-09-29',
    body: `
**you** is the second space along the top. It is everything innernet knows about *you*, as opposed to your projects, and it is the place to check that what it knows is right. Four chips: **summary**, **story**, **mirror**, **private**. ← and → move between them.

## summary: your day, read back

:::shot you-summary the summary: netti's reading of one day.

netti's one-line reading of the day, then **the shape of the day**, **threads you pulled**, **decided**, **left open**, **in your words**, **noticed**, **where it went**, **a question for tomorrow**, and the day hour by hour. ← and → step between days; **open the calendar** jumps. A past day nobody has read yet has **ask netti to read this day**.

## story and mirror

**story** tells your projects as stories. **mirror** sets what innernet holds about you beside what you did: your portrait, the words that keep coming up, when you think, the questions you keep asking. Each line says whose it is: what you said in your own words, what an AI noted about you, or netti's reading. It shows evidence, never a verdict; the conclusion is yours to draw. Private facts are left out of it.

## private: the ledger

:::shot you-private the private ledger. the bar is every fact you have, by who can see it.

**private** is the whole Self Map: *what innernet knows about you, and who gets to see it*. Its sections: the ledger, who you are, by dimension, what netti noticed, what changed, apps, and the raw record. Filter by **private**, **self**, **shared** or **all**, or search what innernet holds.

The bar at the top is every fact, coloured by its class:

| class | who can see it |
| --- | --- |
| **private** | no room or share link ever sees it. an ai tool reads it only when you allow it, and each time is written down |
| **self** | yours to read here. an ai tool reads it only when you allow it, and each time is written down |
| **trusted** | your own tools |
| **work** | your work projects |
| **public** | anyone you share with |

[What each class means over the wire](/docs/personal-memory#disclosure-classes).

## one fact, where it came from

Tap a fact and it opens **where it came from**: the kind of source, the app, the date, and the words themselves. A fact can have many sources; each is kept.

:::shot you-fact a fact, opened: its source, its class, and the three things you can do.

- **edit**: change the words and **who can see it**, then **save this version** (⌘ Enter). The old words stay, locked, as the fact's history.
- **share**: make a card of it, a picture you can post. [Sharing](/docs/sharing)
- **forget**: *forget this for good? every tool stops seeing it, and there is no undo.* Answer **yes, forget it** or **keep it**.

Patterns under *what netti noticed* can be marked **resonates** or **not quite**, which teaches netti what's worth noticing.

## related

- [Personal memory, underneath](/docs/personal-memory)
- [Watching your private facts](/community/guides/starter-you-and-your-facts) (a guide)
`,
  },

  {
    slug: 'inbox-and-fold',
    title: 'the inbox & the fold',
    group: G,
    description: 'what netti did while you were away, how to answer it, and how a memory grows: folds and cells.',
    updated: '2026-09-29',
    body: `
netti works when you aren't looking. The **inbox** is where it tells you what it did, and the one place it asks.

## the fold

A **fold** is one pass where netti changes memory: it takes the captures that arrived since last time and consolidates them, into a project's pages or into facts about you. It replaces what was superseded, records a line of history, and commits. Reading and writing memory are free; folds are what a plan counts.

Folds run on the server, when there is something to fold. Until a capture folds, it waits in the project's **inbox** folder, and retrieval already splices it in, so a thing you said a minute ago is answerable.

## the inbox

:::shot inbox-list activity → inbox: what the last folds did, grouped by day.

**activity** → **inbox**. What arrives:

| arrival | what it means |
| --- | --- |
| **new fact** | netti learned something about you |
| **changed** | a fact or a page moved on: you see *was* → *now* |
| **a line you said** | your own words, kept as evidence for a fact |
| **a pattern** | something netti noticed across many moments |
| **an import** | something you brought in with \`innernet import\` or the import tab |

Grouped by today, yesterday, this week and earlier. A private arrival is blurred until you press **show it**. Open any row for the full words, what it was before, and where it came from; then:

- **see it where it lives** opens it in you → private, lit.
- **share** makes a card of it.
- **dismiss** clears it.
- a pattern asks: **resonates**, or **not quite**.

The other two chips under activity: **river**, the narrated log of everything that happened, and **repos**, every repo tied to a memory.

## cells: how big it is

:::shot inbox-netti the netti space: how much it holds, and how grown it is.

A **cell** is one thing memory holds: a page, a node, a current fact about you. Cells are how innernet measures size, and what netti grows with. You'll see the count on each project tile, on the you line, and in **netti** → **you & netti**: cells, spaces, personal, and how grown netti is.

## when netti isn't sure

Sometimes a fold can't resolve something on its own: who a person is to you, whether a preference is standing or just for today. It asks once. On AI clients that support it the question arrives in the conversation; otherwise it waits here. Answer it and it is never asked again.

## related

- [How it works](/docs/how-it-works)
- [Your inbox and the fold](/community/guides/starter-your-inbox-and-the-fold) (a guide)
`,
  },

  {
    slug: 'sharing',
    title: 'sharing',
    group: G,
    description: 'one page as a link, a fact as a card, a skill to the community. share exactly what you mean to.',
    updated: '2026-09-29',
    body: `
Nothing in innernet is shared until you share it, and sharing is always scoped: a link opens exactly the page you shared, and nothing next to it.

## a link to a page or a folder

:::shot share-studio the share studio: the live page on the left, who it opens for on the right.

1. Open the page or folder and press **share** (bottom left).
2. **what it's called**: the title a visitor sees.
3. **who it opens for**: **anyone with the link**, or **only you**. It takes effect at once, both ways.
4. **the ground this link opens in**: the dial bottom left. Follow your own ground live, or fix a colour for this link.
5. **post it live**. Copy the link, send it, or download its QR code. The studio counts how many times it was opened.

Change anything later and press **update the link**. **let this link go** revokes it; the page stops opening for anyone.

A shared page opens at \`innernet.live/share/…\` wearing your netti and your name, read-only. A shared folder opens as a small read-only space. Anyone arriving can start their own innernet from the foot of the page, and that is the only thing the page asks of them.

## a card of a fact

From the inbox or the private ledger, **share** on a fact, a quote or a pattern opens the **share card**: a picture of it, sized for where you'll post it, with netti on it if you like.

## skills and the community

A **skill** is a page with a manifest: what it lets you do, when to load it, who wrote it. Any AI can read one from a link, and learns the skill.

- **Mark a page as a skill** and it gathers into the project's **skills** folder and the **skills** space.
- **Publish it**: share → **anyone with the link** → post it live. It appears on the [community](/community) under your name, and the link is one anyone can paste into their AI.
- **Bring someone else's**: in any connected AI, hand it a link to a skill (a repo, a gist, a post) and ask to publish it. It lands in the community under **their** name, with yours beside it as who brought it (\`innernet_publish_skill { source_url }\`).
- **Keep one from the community**: ask your AI to find a skill (\`innernet_find_skills\`) and keep it (\`innernet_add_skill\`); a private copy lands in your memory, credited to its author.

:::shot share-link the one thing a skill page asks you to do: paste the link into your ai.

## rooms

A **room** shares a whole memory with people, each with their own access, and a teammate who clones a repo tied to that memory reads it too. Rooms are opening to members gradually; if someone has shared one with you, it appears under **ours**.

## related

- [Share and publish](/community/guides/starter-share-and-publish) (a guide)
- [The community](/community)
`,
  },

  {
    slug: 'chat-window',
    title: 'the chat window',
    group: G,
    description: 'innernet inside your ai chat: one live line that opens into a ledger, and how to set it the way you like.',
    updated: '2026-09-29',
    body: `
In AI clients that can show an app inside the conversation (Claude, ChatGPT and others that support MCP Apps), innernet appears as **one small window**, not a stack of panels. It is how you see, without leaving the chat, what innernet read, kept and held back.

:::shot window-ledger the strip at rest, and the ledger it opens into.

## the strip

At rest it is one line under the answer: netti's mark, what innernet just **found or kept** (never the name of a tool), the counts, a chevron. A new call rolls its line in from below; quiet lines drift through every few seconds.

## the ledger

Tap the strip and it opens in place:

- **the chapter** innernet thinks this conversation is in, and the memory it's working against
- **branch and last commit**, when the memory is tied to a repo
- **the numbers**: read, saved, sent, and **held back** (private facts it did not send)
- **what it found**, as rows. Every row is a door: it opens that exact page of your dashboard.

At the foot: **open in innernet**, **keep it in front**, **let it rest in the chat**.

## setting it

:::shot window-settings settings → in your chats, beside a live preview of the real window.

**settings** → **in your chats**. Every control sits beside the real window, so you see what you set.

| setting | choices |
| --- | --- |
| **where innernet shows up** | **the window** on/off · **the signature line** on/off |
| **when it arrives** | **one line**, or **open** |
| **the line rolls** | on/off |
| **open, the ledger shows** | what it found · the numbers · branch and last commit |
| **a row opens** | **innernet, in a tab**, or **in the window** |

Changes save at once; open chats follow within half a minute. **back to the defaults** resets everything.

## under the hood

The window is one MCP App resource, \`ui://innernet/session.html\`, opened by \`innernet_session\`. It polls gently (every six seconds, slowing down when hidden or unchanged) and is never offered to headless clients like Claude Code or a script. Details: [MCP tools](/docs/mcp-tools#extensions).

## related

- [The chat window, set up](/community/guides/starter-the-chat-window) (a guide)
`,
  },

  {
    slug: 'shortcuts',
    title: 'shortcuts',
    group: G,
    description: 'every key and gesture the dashboard answers to, on one page.',
    updated: '2026-09-29',
    body: `
Everything here can also be done with a tap. These are for when your hands are already on the keys.

## moving

| key or gesture | what it does |
| --- | --- |
| tap a space word | go to that space |
| swipe the row or the page, left or right | the next or previous space |
| ≡ | the whole map of innernet |
| **innernet** in the crumb | back to your projects |
| ← → | the next or previous tab in **you** and **activity**; the next or previous day in **summary** |
| Esc | close whatever is open: the menu, the dial, a sheet, the composer |

## working with things

| key or gesture | what it does |
| --- | --- |
| tap | select |
| double-tap | open |
| double-click a name | rename it (Enter saves, Esc cancels, Shift Enter adds a line) |
| double-click a description | write your own |
| double-click a page's words | edit the page |
| right-click | the tile's menu, or new folder / new page on empty space |
| drag a tile onto a folder | move it inside |
| ⌘ C on a hovered tile | pick it up to move (and copy its link) |
| ⌘ V | put it down in the folder you're in |
| ⌘ Z · ⇧ ⌘ Z | undo · redo a rename or a move |
| press and hold a view chip, then drag | reorder the views; the leftmost is the default |

## facts

| key | what it does |
| --- | --- |
| ⌘ Enter | save the version you're editing |
| ← → in *who can see it* | move between the five classes |

## the docs and the community

| key | what it does |
| --- | --- |
| ⌘ K | search the docs, or the room you're in on the community |

## the Mac app

| key | what it does |
| --- | --- |
| ⌘ ⇧ C | capture what's on your clipboard (change it under settings → app) |
`,
  },
]
